import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { colors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useReportingGenerationsQuery } from "Reporting/Api/Queries/useReportingGenerationsQuery";
import { ChartCard } from "./ChartCard";
import { ChartEmptyState } from "./ChartEmptyState";

const c = colors();

const STATUS_COLORS: Record<string, string> = {
  InProgress: c.InfoMain,
  Done: c.SuccessMain,
  WaitingForApproval: c.WarningMain,
  RejectedByActor: c.ErrorMain,
  AwaitingFunds: c.WarningPale,
  Inactive: c.TextTertiary,
};

const STATUS_TRANSLATION_KEYS: Record<string, (r: typeof Resources) => string> =
  {
    InProgress: (r) => r.Common.StatusInProgress,
    Done: (r) => r.Common.StatusDone,
    WaitingForApproval: (r) => r.Common.StatusWaitingForApproval,
    RejectedByActor: (r) => r.Common.StatusRejectedByActor,
    AwaitingFunds: (r) => r.Common.StatusAwaitingFunds,
    Inactive: (r) => r.Common.StatusInactive,
  };

const getStatusColor = (status: string): string =>
  STATUS_COLORS[status] ?? c.SecondaryLight;

export const JobStatusChart = (): React.ReactNode => {
  const { t } = useResource();
  const { data, isLoading } = useReportingGenerationsQuery();

  const chartData = (data?.jobStatusDistribution ?? []).map((item) => ({
    ...item,
    translatedStatus: STATUS_TRANSLATION_KEYS[item.status]
      ? t(STATUS_TRANSLATION_KEYS[item.status](Resources))
      : item.status,
  }));
  const total = chartData.reduce((sum, item) => sum + item.count, 0);
  const isEmpty = chartData.length === 0;

  return (
    <ChartCard
      title={t(Resources.Reporting.JobStatusDistribution)}
      isLoading={isLoading}
      height={350}
    >
      {isEmpty ? (
        <ChartEmptyState />
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="translatedStatus"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={2}
              strokeWidth={0}
            >
              {chartData.map((entry) => (
                <Cell key={entry.status} fill={getStatusColor(entry.status)} />
              ))}
              <Label
                value={total}
                position="center"
                style={{
                  fontSize: "24px",
                  fontWeight: 700,
                  fill: c.White,
                }}
              />
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: c.BackgroundTile,
                border: `1px solid ${c.Border}`,
                borderRadius: 8,
              }}
              itemStyle={{ color: c.White }}
            />
            <Legend
              wrapperStyle={{
                color: c.TextSecondary,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};
